import React from "react";
import PropTypes from "prop-types";
import Downshift from "downshift";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";

import { fetchRedirectionParams } from "../../../actions/redirect";
import { FormatAmount } from "../../../utils/Formatter";
import { calculateAmount } from "../../../utils/SurchargeUtils";
import BankService from "./BankService";
import "./Bank.css";

const styles = theme => ({
  bankNameSelected: {
    color: theme.palette.text.primary,
    padding: `15px 0`,
    borderBottom: `2px solid ${theme.palette.secondary.main}`
  },
  bankName: {
    color: theme.palette.text.secondary,
    padding: `15px 0`
  },
  payBtnColor: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    border: `solid`
  }
});

export class Bank extends React.Component {
  constructor(props) {
    super(props);
    const { savedDetails } = props.charge;

    if (savedDetails) {
      const { lastBank } = savedDetails;
      const { label } = BankService.all().find(b => b.code === lastBank);
      this.state = {
        search: label,
        bankCode: lastBank
      };
    } else {
      this.state = { search: "" };
    }
  }

  handleSearch(search) {
    const bank = BankService.all().find(b => b.label === search);
    if (bank) this.setState({ bankCode: bank.code, search, showError: false });
    else this.setState({ search, bankCode: undefined, showError: false });
  }

  handleSelect(selection) {
    const { label } = BankService.all().find(b => b.code === selection.code);
    this.setState({
      bankCode: selection.code,
      search: label,
      showError: false
    });
  }

  submitNbForm() {
    const type = "nb";
    const { dispatch, charge, history } = this.props;
    const { bankCode } = this.state;
    if (!bankCode) {
      this.setState({ showError: true });
      return;
    }
    const form = { bankCode };
    dispatch(fetchRedirectionParams(charge.uuid, form, type, history));
  }

  static renderBank(bankProps) {
    const {
      bank,
      index,
      itemProps,
      highlightedIndex,
      selectedItem
    } = bankProps;
    const isHighlighted = highlightedIndex === index;
    const isSelected = (selectedItem || "").indexOf(bank.label) > -1;

    return (
      <MenuItem
        {...itemProps}
        key={bank.label}
        selected={isHighlighted}
        style={{
          fontWeight: isSelected ? 500 : 400
        }}
      >
        {bank.label}
      </MenuItem>
    );
  }

  renderAutocompleteSearch() {
    const { search, showError } = this.state;
    return (
      <Downshift
        onInputValueChange={value => this.handleSearch(value)}
        inputValue={search}
      >
        {({
          clearSelection,
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          highlightedIndex,
          inputValue,
          isOpen,
          openMenu,
          selectedItem
        }) => {
          const { onBlur, onClick, ...inputProps } = getInputProps({
            onChange: event => {
              if (event.target.value === "") {
                clearSelection();
              }
            },
            onClick: openMenu,
            id: "search-bar",
            placeholder: "Search for a bank"
          });

          return (
            <div>
              <TextField
                fullWidth
                helperText={showError ? "Please select a bank" : ""}
                error={showError}
                label="Bank"
                InputLabelProps={getLabelProps({ shrink: true })}
                onBlur={onBlur}
                onClick={onClick}
                inputProps={{
                  ...inputProps // ✅ NOW applied to <input>
                }}
                // InputProps={{
                //   onBlur,
                //   onClick,
                //   ...inputProps
                // }}
              />
              <Paper
                className="bank-suggestions"
                square
                elevation={3}
                {...getMenuProps({}, { suppressRefError: true })}
              >
                {isOpen
                  ? BankService.search(inputValue).map((bank, index) =>
                      Bank.renderBank({
                        bank,
                        index,
                        itemProps: getItemProps({ item: bank.label }),
                        highlightedIndex,
                        selectedItem
                      })
                    )
                  : null}
              </Paper>
            </div>
          );
        }}
      </Downshift>
    );
  }

  render() {
    const { charge, redirecting, classes, history } = this.props;
    const { uuid, due, currency, paymentFamilies } = charge;
    const { bankCode } = this.state;

    const totalAmount = FormatAmount(
      calculateAmount(
        due,
        history.location.pathname,
        paymentFamilies.surcharges
      ),
      currency
    );

    return (
      <div>
        {this.renderAutocompleteSearch()}
        <Grid container spacing={2} className="popular-banks-grid">
          {BankService.popular().map(bank => {
            const isSelected = bankCode === bank.code;
            return (
              <Grid
                item
                xs={6}
                sm={3}
                className={
                  isSelected ? "popular-bank-selected" : "popular-bank"
                }
                key={bank.code}
              >
                <Card elevation={isSelected ? 3 : 1} id={`bank-${bank.code}`}>
                  <CardActionArea onClick={() => this.handleSelect(bank)}>
                    <img
                      alt={bank.label}
                      src={bank.icon}
                      className="bank-logo"
                    />
                    <Typography
                      variant="subtitle1"
                      className={
                        isSelected
                          ? `${classes.bankNameSelected}`
                          : `${classes.bankName}`
                      }
                    >
                      {bank.label}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Grid container>
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <input type="hidden" name="uuid" value={uuid} />
            <Button
              fullWidth
              id="pay-button"
              variant="outlined"
              color="primary"
              className={classes.payBtnColor}
              disabled={redirecting}
              onClick={() => this.submitNbForm()}
            >
              {redirecting ? (
                <CircularProgress color="inherit" size={30} />
              ) : (
                `Pay ${totalAmount}`
              )}
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  }
}

Bank.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    savedDetails: PropTypes.shape({
      lastBank: PropTypes.string
    }),
    due: PropTypes.number.isRequired,
    currency: PropTypes.string,
    paymentFamilies: PropTypes.shape({
      surcharges: PropTypes.arrayOf(PropTypes.shape())
    }).isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  redirecting: PropTypes.bool.isRequired,
  history: PropTypes.shape().isRequired,
  // eslint-disable-next-line react/require-default-props
  classes: PropTypes.shape()
};

function mapStateToProps({ redirectionReducer }) {
  return redirectionReducer;
}

export default withStyles(styles, { withTheme: true })(
  withRouter(connect(mapStateToProps)(Bank))
);
